"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _label = _interopRequireWildcard(require("@ux/label"));
var _text = _interopRequireDefault(require("@ux/text"));
var _chevronDown = _interopRequireDefault(require("@ux/icon/chevron-down"));
var _useControllableState = _interopRequireDefault(require("@ux/component-utilities/use-controllable-state"));
const _excluded = ["children", "className", "defaultValue", "disabled", "id", "invalid", "label", "name", "placeholder", "onBlur", "onChange", "required", "size", "stretch", "value", "visualSize"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const textSize = {
  md: 0,
  sm: -1
};
const UX_SELECT_CLASSNAME = 'ux-select';

/**
 * Creates the select component
 *
 * @param {Object} props - Properties to prepare the Select component
 * @param {Elements} props.children - Option elements for the select
 * @param {String} [props.className] - Class name for layout
 * @param {String} [props.defaultValue] - Default value for the select
 * @param {Boolean} [props.disabled] - Sets the component as disabled
 * @param {String} props.id - Adds an id to the select
 * @param {Boolean} [props.invalid] - Sets the component as invalid
 * @param {Node} [props.label] - Sets a floating label
 * @param {String} props.name - Provides a name to the component
 * @param {String} [props.placeholder] - Provides a placeholder option within the select
 * @param {Function} [props.onBlur] - Callback for when select loses focus
 * @param {Function} [props.onChange] - Callback for when the value changes
 * @param {Boolean} [props.required] - Sets the component as required
 * @param {Integer} [props.size] - Sets the size of the component
 * @param {Boolean} [props.stretch] - Stretch the width of the container
 * @param {Node} [props.value] - The value for the select
 * @param {('sm' | 'md')} [props.visualSize] - Sets component size, passed via ref from the convenience component
 * @param {Object} ref - Element reference for validation
 * @returns {Component} - The select component
 */
const Select = /*#__PURE__*/_react.default.forwardRef(function Select(_ref, ref) {
  let {
      children,
      className,
      defaultValue: defaultProp = '',
      disabled,
      id,
      invalid,
      label,
      name,
      placeholder,
      onBlur,
      onChange: onControllableChange = Function.prototype,
      required,
      size,
      stretch = true,
      value: controllableProp,
      visualSize
    } = _ref,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const {
    multiple
  } = rest;
  delete rest.style;

  // Ensure ref
  const selectRef = (0, _react.useRef)({});
  (0, _react.useImperativeHandle)(ref, () => selectRef.current, [selectRef]);

  // Settings for the display text
  const [selectedText, setSelectedText] = (0, _react.useState)('');
  const [value, onChange] = (0, _useControllableState.default)({
    prop: controllableProp,
    defaultProp,
    onChange: onControllableChange
  });
  const _onChange = (0, _react.useCallback)(ev => {
    const val = multiple ? Array.from(ev.target.selectedOptions, option => option.value) : ev.target.value;
    onChange(val, {
      event: ev
    });
  }, [multiple, onChange]);
  (0, _react.useEffect)(() => {
    var _selectRef$current;
    const {
      selectedOptions
    } = selectRef.current;
    const selected = selectedOptions ? Array.from(selectedOptions, _ref2 => {
      let {
        textContent,
        label: l
      } = _ref2;
      return textContent || l;
    }).filter(Boolean).join(', ') : '';
    setSelectedText(selected);
    if (typeof (selectRef === null || selectRef === void 0 ? void 0 : (_selectRef$current = selectRef.current) === null || _selectRef$current === void 0 ? void 0 : _selectRef$current.setAttribute) !== 'function') return;
    selectRef.current.setAttribute('value', [].concat(value).join(','));
  }, [setSelectedText, value]);
  const classNames = (0, _classnames.default)(`${UX_SELECT_CLASSNAME}-shell`, `${UX_SELECT_CLASSNAME}--size-${visualSize}`, className, {
    // Flag for when the value is filled (something is selected)
    [`${UX_SELECT_CLASSNAME}--filled`]: value || placeholder,
    // Stretch the width of the container
    [`${UX_SELECT_CLASSNAME}--stretch`]: stretch && typeof size !== 'number'
  });
  if (!children) {
    return null;
  }
  return /*#__PURE__*/_react.default.createElement(_text.default.div, {
    ref: ref,
    className: classNames,
    size: textSize[visualSize],
    as: "input",
    emphasis: invalid ? 'critical' : null,
    "data-version": "2400",
    "aria-disabled": disabled,
    tabIndex: disabled ? -1 : 0,
    style: {
      '--uxSelect-field-size': typeof size === 'number' ? `${size}ch` : null
    }
  }, !placeholder && (0, _label.ensureLabelComponent)(label, {
    float: Boolean(selectedText),
    internal: true,
    required,
    htmlFor: id
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: `${UX_SELECT_CLASSNAME}-text`
  }, selectedText), /*#__PURE__*/_react.default.createElement(_chevronDown.default, {
    className: `${UX_SELECT_CLASSNAME}-accessory`
  }), /*#__PURE__*/_react.default.createElement("select", (0, _extends2.default)({
    "aria-invalid": invalid,
    "aria-required": Boolean(required),
    className: `${UX_SELECT_CLASSNAME}-field`,
    disabled: disabled,
    id: id,
    multiple: multiple,
    name: name,
    onBlur: onBlur,
    onChange: _onChange,
    ref: selectRef,
    value: value
  }, rest), placeholder && /*#__PURE__*/_react.default.createElement("option", {
    disabled: true,
    value: ""
  }, placeholder), children));
});
Select.propTypes = {
  /** Children to render as siblings */
  children: _propTypes.default.node.isRequired,
  /** Class for the root component */
  className: _propTypes.default.string,
  /** Provides default value */
  defaultValue: _propTypes.default.string,
  /** Sets the component as disabled */
  disabled: _propTypes.default.bool,
  /** Provides an id to the select */
  id: _propTypes.default.string.isRequired,
  /** Sets the component as invalid */
  invalid: _propTypes.default.bool,
  /** Provides a floating label */
  label: _propTypes.default.node,
  /** Allow for multiple selections (native html) */
  multiple: _propTypes.default.bool,
  /** Provides a name attribute to the select */
  name: _propTypes.default.string.isRequired,
  /** Fires when the select loses focus */
  onBlur: _propTypes.default.func,
  /** Fires when the value changes */
  onChange: _propTypes.default.func,
  /** Provides a placeholder, cannot be used with floating label */
  placeholder: _propTypes.default.node,
  /** Sets the select as required */
  required: _label.default.propTypes.required,
  /** Set the size of the select */
  size: _propTypes.default.number,
  /** Stretch the width of the container */
  stretch: _propTypes.default.bool,
  /** Value for the select */
  value: _propTypes.default.node,
  /** Component size, passed via ref from the convenience component */
  visualSize: _propTypes.default.oneOf(['sm', 'md'])
};
Select.displayName = 'Select';
var _default = exports.default = Select;